import pauseIcon from '@/assets/icons/pause.svg';
import playIcon from '@/assets/icons/play.svg';
import { Typography } from '@/shared';
import { useBreakpoints } from '@/shared/providers/Device';
import { useMicrocopy } from '@/shared/providers/Microcopy/MicrocopyHook';
import { useThemeSection } from '@/shared/providers/SectionContext';
import { ArticleVideoStoryblok } from '@/shared/types/storyblok/storyblok-types';
import clsx from 'clsx';
import { VideoJsonLd } from 'next-seo';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player/file';
import Container from '../Container/Container';
import RichText from '../RichText/RichText';
import styles from './ArticleVideo.module.scss';

const ArticleVideo = (data: ArticleVideoStoryblok) => {
    const { filename, name, title, uploadDate, copyright } = data.blok.video;
    const isDark = useThemeSection() === 'Dark' ?? data.blok.isDark;
    const { isExtraLarge, isLarge } = useBreakpoints();
    const isMobile = useMemo(() => !isExtraLarge && !isLarge, [isExtraLarge, isLarge]);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [overlay, setOverlay] = useState<boolean>(true);
    const { microcopy } = useMicrocopy();
    const curDateTime = new Date();
    const gmtString = curDateTime.toUTCString();

    const togglePlayPause = () => {
        setIsPlaying(!isPlaying);
        if (isMobile) {
            setOverlay(true);
        }
    };

    const handleOverflow = (show: boolean) => {
        if (show) {
            setOverlay(true);
        } else {
            if (isPlaying) {
                setOverlay(false);
            }
        }
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (isMobile && isPlaying && overlay) {
            timer = setTimeout(() => {
                setOverlay(false);
            }, 1000);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [overlay, isPlaying, isMobile]);

    return (
        <>
            <VideoJsonLd
                name={name || title}
                description={data.blok.videoDescription}
                contentUrl={filename}
                embedUrl={filename}
                uploadDate={uploadDate ?? gmtString}
                thumbnailUrl={data.blok.thumbnail?.filename ?? ''}
            />
            <Container code={'large'} blok={data?.blok} className={{ wrap: styles.container }}>
                <div
                    className={styles.video}
                    onClick={togglePlayPause}
                    onMouseEnter={() => handleOverflow(true)}
                    onMouseLeave={() => handleOverflow(false)}
                >
                    <ReactPlayer
                        width={'100%'}
                        height={'100%'}
                        url={data.blok.video.filename}
                        alt={data.blok.video.alt || ''}
                        playing={isPlaying}
                        playsinline
                        onEnded={() => {
                            setIsPlaying(false);
                            setOverlay(true);
                        }}
                    />
                    <div
                        className={clsx(
                            styles.controls,
                            overlay ? clsx(styles.overlay, { [styles.isPlaying]: isPlaying }) : styles.hide,
                        )}
                    >
                        <Image priority src={isPlaying ? pauseIcon : playIcon} alt="control-button" />
                    </div>
                </div>
                <Container
                    code="standard"
                    className={{ wrap: clsx(styles.description, isDark ? styles.dark : styles.light) }}
                >
                    {copyright && (
                        <Typography.Small darkTheme={isDark} className={styles.sourcecolor}>
                            {`${microcopy.videoSourceText} ${data.blok.video.copyright}`}
                        </Typography.Small>
                    )}
                    {data.blok.videoDescription && (
                        <RichText
                            document={data.blok.videoDescription}
                            classNameRoot={clsx(styles.desccolor, isDark && styles.isDark)}
                        />
                    )}
                </Container>
            </Container>
        </>
    );
};

export default ArticleVideo;
